import * as React from 'react';

import { ActivityPacing } from '@showbie-socrative/socrative-utils';
import { useSelector } from 'react-redux';

import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { parseResultData } from '~teacher/components/ActivityResult/util/parseResultData';
import { NoStudentsMessage } from '~teacher/components/NoStudentsMessage';
import { ResultsGrid } from '~teacher/components/ResultsGrid';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useIsQuickQuestion } from '~teacher/hook/';
import {
  Activity,
  ActivitySettingsCamelCase,
  ApiQuestionResponse,
  Room,
  Student,
} from '~teacher/models/activities';
import { Quiz, QuizAnswer, QuizQuestion } from '~teacher/models/quizzes';
import { selectCurrentRoomIsShowbie } from '~teacher/slices/teacher/teacherSelector';

import { useResultState } from './ActivityResultProvider';
import { normalizeQuestions } from './util/normalizeQuestions';
import { normalizeResponses } from './util/normalizeResponses';
import { normalizeStudent } from './util/normalizeStudent';

export interface Props {
  activity: Activity;
  room: Room;
  students: Record<string, Student>;
  responses: Record<string, ApiQuestionResponse>;
  quiz: Quiz;
  quizQuestion: Record<string, QuizQuestion>;
  quizAnswer: Record<string, QuizAnswer>;
  showInsights?: boolean;
}

const ActivityResultLive = ({ showInsights = false, ...props }: Props) => {
  const teacherApplicationContext = useTeacherApplicationContext();
  const socrativeRouter = useSocrativeRouter();
  const { questionScores } = useResultState();
  const isShowbieRoom: boolean = useSelector(selectCurrentRoomIsShowbie);
  const activity = props.activity;
  const settings = activity.settings;
  const hasTimers =
    ((settings as unknown as ActivitySettingsCamelCase)?.quizTimerSeconds ?? -1) >
    0;

  const questions = React.useMemo(
    () => normalizeQuestions(props.quizQuestion),
    [props.quizQuestion]
  );
  const responses = React.useMemo(
    () =>
      normalizeResponses({
        students: props.students,
        responses: props.responses,
        quizQuestion: props.quizQuestion,
      }),
    [props.students, props.responses, props.quizQuestion]
  );
  const students = React.useMemo(
    () => normalizeStudent(props.students),
    [props.students]
  );

  const isQuickQuestion = useIsQuickQuestion(activity.activityType);

  const parsedActivityData = parseResultData({
    questions,
    activity: activity,
    answers: props.quizAnswer,
    responses,
    students,
    questionScores: questionScores,
  });

  // Redirect to result questions if needed
  React.useEffect(() => {
    if (settings.pacing === ActivityPacing.Teacher) {
      socrativeRouter.routeToResultsQuestion(
        activity.id,
        activity.activityState < 0 ? 1 : activity.activityState + 1,
        true
      );
    } else if (isQuickQuestion) {
      socrativeRouter.routeToResultsQuestion(activity.id, 1);
    }
  }, [
    settings.pacing,
    activity.id,
    activity.activityState,
    isQuickQuestion,
    socrativeRouter,
  ]);

  return (
    <>
      <ResultsGrid
        showInsights={showInsights}
        resultsTableQuestions={parsedActivityData.resultsTableQuestions}
        participants={parsedActivityData.participants}
        maxScore={parsedActivityData.maxScore}
        activityInstanceId={parsedActivityData.activityInstanceId}
        activityStage={parsedActivityData.activityStage}
        isRosteredRoom={props.room.isRostered}
        passRate={parsedActivityData.passRate}
        totalStudents={activity.totalStudents}
        presentStudents={activity.presentStudents}
        completionRate={activity.completionRate}
        averageScoreRate={activity.averageScoreRate}
        passingStudentsRate={activity.passingStudentsRate}
        hasTimers={hasTimers}
        quizTimerDurationSeconds={
          ((settings as unknown as ActivitySettingsCamelCase)?.quizTimerSeconds ?? 0)
        }
      />
      {isShowbieRoom === false &&
      teacherApplicationContext === TeacherApplicationContext.Socrative &&
      // Show the invite button if 0 students have responded to the quiz
      Object.values(responses.byStudentId).length === 0 ? (
        <NoStudentsMessage roomName={props.room.nameLower} />
      ) : null}
    </>
  );
};

export { ActivityResultLive };
